import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { createTutorSanad } from "../../../api/endpoints/tutors/sanad";
import Modal from "../ModalWithFooter";
import { onDirectUpload, showApiErrorMessages } from "../../../helpers";
import DropDown from "../../forms/dropdown/DropDown";
import FileDropZone from "../../forms/FileDropZone";
import { ApiResponse } from "../../../api/apiResponse";
import toast from "react-hot-toast";
import { useTelawah } from "../../../hooks/useTelawah";

type AddSanadModalProps = {
  setModalState: (state: boolean) => void;
  modalState: boolean;
};
export default function AddSanadModal(props: AddSanadModalProps) {
  const { setModalState, modalState } = props;
  const LocalBasePath = "page.sanadInfo";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const [isUploading, setIsUploading] = useState(false);
  const [telawahCurrent, setTelawahCurrent] = useState<any>(null);
  const [telawahType, setTelawahType] = useState<any>(null);
  const [signedId, setSignedId] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const { telawahOptions } = useTelawah();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm();

  const onSubmit = async (data: any) => {
    if (!file) {
      toast.error(t("pleaseUpload"));
      return;
    }
    const submitedSanad = await createTutorSanad({
      sanad_type: data.sanadType, // possible values from_quraan | memorized
      file: signedId,
      telawah_id: data.recitation,
    }).then((response: ApiResponse) => {
      if (!response.isSuccess) {
        return showApiErrorMessages(response);
      }
      toast.success(t("savedSuccessfully"));
      setIsUploading(false);
      setFile(null);
      setSignedId(null);
      setTelawahCurrent(null);
      setTelawahType(null);
      setValue("recitation", null);
      setValue("sanadType", null);

      if (data.addClose) {
        setModalState(false);
      }
    });
  };

  const onUploadSuccess = (signedId: string) => {
    setSignedId(signedId);
  };

  const onDropFile = (selectedFile: File) => {
    setIsUploading(true);
    setFile(selectedFile);
    onDirectUpload(selectedFile, onUploadSuccess);
  };
  const onDeleteFile = () => {
    setFile(null);
    setIsUploading(false);
  };
  return (
    <Modal open={modalState} setOpen={setModalState}>
      <Modal.Body setOpen={setModalState} title={t("addSanad")}>
        <form
          id="addSanad"
          onSubmit={handleSubmit(onSubmit)}
          data-cy={"sanadModel"}
        >
          <DropDown
            id="recitation"
            name="recitation"
            schema={{ required: true }}
            register={register}
            setValue={(value: any) => {
              setValue("recitation", value);
              setTelawahCurrent(value);
            }}
            cypressSelector={"recitation"}
            defaultValue={telawahCurrent}
            label={t("recitation")}
            placeholder={t("chooseRecitation")}
            error={!!errors.sponsorshipRegistry}
            options={telawahOptions ?? []}
          />
          <DropDown
            id="sanadType"
            name="sanadType"
            className="mt-6"
            cypressSelector={"sanadType"}
            schema={{ required: true }}
            register={register}
            setValue={(value: any) => {
              setValue("sanadType", value);
              setTelawahType(value);
            }}
            defaultValue={telawahType}
            label={t("sanadType")}
            placeholder={t("chooseType")}
            error={!!errors.sponsorshipRegistry}
            options={[
              {
                key: t("from_quraan"),
                value: "from_quraan",
              },
              {
                key: t("memorized"),
                value: "memorized",
              },
            ]}
          />
          <div className="mt-6">
            <label
              htmlFor={"ASfdd"}
              className="block text-sm font-medium text-gray-700"
            >
              {t("sanadCertificate")} *
            </label>
            <FileDropZone
              onUpload={(file: File) => {
                onDropFile(file);
              }}
              fileUploaded={file}
              onDelete={onDeleteFile}
              isUploading={setIsUploading}
              signedId={signedId}
            />
          </div>
          {!signedId && (
            <div className="mt-6 text-sm font-medium text-gray-500">
              <p>{t("addSanadFooter")}</p>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="submit"
          form="addSanad"
          className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
          onClick={() => setValue("addClose", true)}
          data-cy={"saveSanadModel"}
        >
          {t("save")}
        </button>
        <button
          type="submit"
          form="addSanad"
          data-cy={"addSanadModel"}
          className="mx-4 inline-flex justify-center rounded-md border border-transparent disabled:bg-gray-600 bg-brandblue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
          onClick={() => setValue("addClose", false)}
        >
          {t("add")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
